import { useEffect, type ReactNode } from 'react';

import { Button } from './Button';

export interface ConfirmDialogProps {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'destructive';
  isBusy?: boolean;
  testId?: string;
  children?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  isBusy = false,
  testId = 'confirm-dialog',
  children,
  onCancel,
  onConfirm,
}: ConfirmDialogProps): JSX.Element {
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isBusy) onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel, isBusy]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      data-testid={testId}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-5 shadow-lg">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        {body ? (
          <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
            {body}
          </p>
        ) : null}
        {children}
        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isBusy}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'primary'}
            onClick={onConfirm}
            disabled={isBusy}
            data-testid={`${testId}-confirm`}
          >
            {isBusy ? '…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
